import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiCheck, 
  FiLoader, 
  FiAlertCircle,
  FiCalendar,
  FiUser,
  FiMessageSquare,
  FiCheckCircle,
  FiHome,
  FiShield,
  FiArrowLeft,
  FiX
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import logo from '../../assets/T.png';
import api from '../../api';

// Google reCAPTCHA site key
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

const DemoRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submittedData, setSubmittedData] = useState(null);
  
  // reCAPTCHA & Modal states
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');
  
  const [formData, setFormData] = useState({
    institutionName: '',
    institutionEmail: '',
    institutionPhone: '',
    institutionType: '',
    otherInstitutionType: '',
    pocName: '',
    pocEmail: '',
    pocPhone: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
    termsAccepted: false
  });

  const institutionTypes = [
    { value: 'school', label: 'School (K-12)' },
    { value: 'pu_college', label: 'PU College' },
    { value: 'degree_college', label: 'Degree College' },
    { value: 'coaching_center', label: 'Coaching Center' },
    { value: 'university', label: 'University' },
    { value: 'others', label: 'Others' }
  ];

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
    '04:00 PM', '04:30 PM', '05:00 PM'
  ];

  // Render reCAPTCHA inside the modal when it opens
  useEffect(() => {
    if (showCaptchaModal) {
      setTimeout(() => {
        const captchaContainer = document.getElementById('recaptcha-submission-container');
        if (captchaContainer && window.grecaptcha) {
          captchaContainer.innerHTML = ''; // Clear previous renders
          window.grecaptcha.render('recaptcha-submission-container', {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: (token) => {
              setRecaptchaToken(token);
              setRecaptchaError('');
            },
            'expired-callback': () => {
              setRecaptchaToken('');
              setRecaptchaError('Verification expired. Please verify again.');
            },
            'error-callback': () => {
              setRecaptchaError('Verification failed. Please try again.');
            }
          });
        }
      }, 100);
    }
  }, [showCaptchaModal]);

  const handleLegalLinkClick = (e, path) => {
    e.preventDefault();
    sessionStorage.setItem('demoRequestFormData', JSON.stringify(formData));
    navigate(path);
  };

  useEffect(() => {
    const savedData = sessionStorage.getItem('demoRequestFormData');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
        sessionStorage.removeItem('demoRequestFormData');
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setFormError('');
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    const fullNumber = phone.startsWith('+') ? phone : `+${phone}`;
    const phoneNumber = parsePhoneNumberFromString(fullNumber);
    return phoneNumber && phoneNumber.isValid();
  };

  // Initial Form Check before showing reCAPTCHA modal
  const handleInitiateSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.institutionName.trim()) {
      setFormError('Please enter your institution name');
      return;
    }
    if (!formData.institutionEmail.trim() || !formData.institutionEmail.includes('@')) {
      setFormError('Please enter a valid institution email address');
      return;
    }
    if (!validatePhone(formData.institutionPhone)) {
      setFormError('Please enter a valid institution phone number');
      return;
    }
    if (!formData.institutionType) {
      setFormError('Please select your institution type');
      return;
    }
    if (formData.institutionType === 'others' && !formData.otherInstitutionType.trim()) {
      setFormError('Please specify your institution type');
      return;
    }
    if (!formData.pocName.trim()) {
      setFormError('Please enter the point of contact name');
      return;
    }
    if (!formData.pocEmail.trim() || !formData.pocEmail.includes('@')) {
      setFormError('Please enter a valid point of contact email address');
      return;
    }
    if (!validatePhone(formData.pocPhone)) {
      setFormError('Please enter a valid point of contact phone number');
      return;
    }
    if (!formData.preferredDate) {
      setFormError('Please pick a date for the demo');
      return;
    }
    if (!formData.preferredTime) {
      setFormError('Please pick a time for the demo');
      return;
    }
    if (!formData.termsAccepted) {
      setFormError('Please agree to the terms and conditions to continue');
      return;
    }

    // Open verification modal on successful validation
    setShowCaptchaModal(true);
  };

  // Final API Submission after reCAPTCHA verification
  const handleFinalSubmit = async () => {
    if (!recaptchaToken) {
      setRecaptchaError('Please complete the verification check');
      return;
    }

    setLoading(true);
    setRecaptchaError('');

    try {
      const response = await api.post('/api/public/demo-request', {
        ...formData,
        recaptchaToken
      });
      setSubmittedData(response.data.data);
      setShowCaptchaModal(false);
      setFormSubmitted(true);
    } catch (error) {
      console.error('Error submitting demo request:', error);
      setFormError(error.response?.data?.message || 'Something went wrong. Please try sending again.');
      setShowCaptchaModal(false);
      setRecaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    return minDate.toISOString().split('T')[0];
  };

  // Success State View
  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-between">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <img src={logo} alt="Truyon Logo" className="w-8 h-8 object-contain" />
              <span className="text-xl font-bold tracking-tight text-slate-900">truyon</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
              <FiArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>

        <main className="flex-grow flex items-center justify-center px-4 py-8 sm:py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-xl bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
          >
            <div className="bg-emerald-600 px-6 py-10 text-center text-white">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4 backdrop-blur-sm">
                <FiCheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Demo Request Sent!</h1>
              <p className="text-emerald-100 font-medium text-sm mt-2">
                Reference Code: #{submittedData?.requestId?.slice(-6).toUpperCase()}
              </p>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              <p className="text-slate-600 text-center text-base sm:text-lg">
                Thank you for your interest! Our team is reviewing your details and will get in touch within 24 hours to confirm your demo.
              </p>

              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200/60 space-y-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">What happens next?</h2>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 font-bold text-xs rounded-full flex items-center justify-center mt-0.5">1</span>
                    <span>Our team reviews your institution setup details.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 font-bold text-xs rounded-full flex items-center justify-center mt-0.5">2</span>
                    <span>We call or email you to confirm the time slot.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 font-bold text-xs rounded-full flex items-center justify-center mt-0.5">3</span>
                    <span>You receive an email with your video link for the demo.</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link
                  to="/"
                  className="w-full text-center py-3.5 px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold transition-colors"
                >
                  Return to Home
                </Link>
                <button
                  onClick={() => {
                    setFormSubmitted(false);
                    setFormData({
                      institutionName: '',
                      institutionEmail: '',
                      institutionPhone: '',
                      institutionType: '',
                      otherInstitutionType: '',
                      pocName: '',
                      pocEmail: '',
                      pocPhone: '',
                      preferredDate: '',
                      preferredTime: '',
                      message: '',
                      termsAccepted: false
                    });
                    setRecaptchaToken('');
                  }}
                  className="w-full text-center py-3.5 px-6 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors"
                >
                  Submit Another Request
                </button>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={logo} alt="Truyon Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold tracking-tight text-slate-900">truyon</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
            <FiArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Main Layout - Maximized Form Width */}
      <main className="flex-grow w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="mb-6 text-center sm:text-left">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full uppercase tracking-wider mb-2">
            Live Demo Request
          </span>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Schedule a Personal Demo
          </h1>
          <p className="mt-1 text-slate-600 text-sm sm:text-base">
            See how Truyon ERP works for your institution with a customized walkthrough.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl border border-slate-200/80 overflow-hidden"
        >
          <div className="p-5 sm:p-8 lg:p-10">
            
            {formError && (
              <div className="mb-6 bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3 text-rose-700 text-sm">
                <FiAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">Error: </span>
                  {formError}
                </div>
              </div>
            )}

            <form onSubmit={handleInitiateSubmit} className="space-y-8">
              
              {/* Section 1: Institution Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                  <FiHome className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-slate-900">1. Institution Details</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Institution Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="institutionName"
                      value={formData.institutionName}
                      onChange={handleChange}
                      placeholder="e.g. National Institute of Science"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Institution Email <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="institutionEmail"
                      value={formData.institutionEmail}
                      onChange={handleChange}
                      placeholder="admin@institution.edu"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Institution Phone <span className="text-rose-500">*</span>
                    </label>
                    <PhoneInput
                      country={'in'}
                      value={formData.institutionPhone}
                      onChange={(value) => {
                        const formattedValue = value.startsWith('+') ? value : `+${value}`;
                        setFormData(prev => ({ ...prev, institutionPhone: formattedValue }));
                      }}
                      placeholder="+91 98765 43210"
                      inputProps={{ name: 'institutionPhone' }}
                      containerClass="w-full"
                      inputClass="!w-full !text-sm !h-[46px] !bg-slate-50 !border !border-slate-200 !rounded-xl focus:!bg-white focus:!ring-2 focus:!ring-indigo-500 focus:!border-transparent"
                      buttonClass="!border !border-slate-200 !rounded-l-xl !bg-slate-100"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Institution Type <span className="text-rose-500">*</span>
                    </label>
                    <select
                      name="institutionType"
                      value={formData.institutionType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    >
                      <option value="">Select type...</option>
                      {institutionTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.institutionType === 'others' && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Specify Institution Type <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="otherInstitutionType"
                      value={formData.otherInstitutionType}
                      onChange={handleChange}
                      placeholder="e.g. Vocational Center"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    />
                  </div>
                )}
              </div>

              {/* Section 2: Point of Contact Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                  <FiUser className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-slate-900">2. Point of Contact Details</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Point of Contact Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="pocName"
                      value={formData.pocName}
                      onChange={handleChange}
                      placeholder="Full Name"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Point of Contact Email <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="pocEmail"
                      value={formData.pocEmail}
                      onChange={handleChange}
                      placeholder="name@institution.edu"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    />
                  </div>

                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Point of Contact Phone <span className="text-rose-500">*</span>
                    </label>
                    <PhoneInput
                      country={'in'}
                      value={formData.pocPhone}
                      onChange={(value) => {
                        const formattedValue = value.startsWith('+') ? value : `+${value}`;
                        setFormData(prev => ({ ...prev, pocPhone: formattedValue }));
                      }}
                      placeholder="+91 98765 43210"
                      inputProps={{ name: 'pocPhone' }}
                      containerClass="w-full"
                      inputClass="!w-full !text-sm !h-[46px] !bg-slate-50 !border !border-slate-200 !rounded-xl focus:!bg-white focus:!ring-2 focus:!ring-indigo-500 focus:!border-transparent"
                      buttonClass="!border !border-slate-200 !rounded-l-xl !bg-slate-100"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Preferred Schedule */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                  <FiCalendar className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-slate-900">3. Preferred Schedule</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Pick Date <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={getMinDate()}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Pick Time Slot <span className="text-rose-500">*</span>
                    </label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                    >
                      <option value="">Select time...</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 4: Optional Message */}
              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <FiMessageSquare className="w-4 h-4 text-indigo-600" />
                  Any questions or specific requirements? (Optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us what tools you currently use or specific features you want to see..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all resize-none"
                />
              </div>

              {/* Terms Checkbox */}
              <div className="pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    I agree to the{' '}
                    <a 
                      href="/terms-of-service" 
                      className="text-indigo-600 font-semibold hover:underline"
                      onClick={(e) => handleLegalLinkClick(e, '/terms-of-service')}
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a 
                      href="/privacy-policy" 
                      className="text-indigo-600 font-semibold hover:underline"
                      onClick={(e) => handleLegalLinkClick(e, '/privacy-policy')}
                    >
                      Privacy Policy
                    </a>
                    . I agree that Truyon can contact me regarding this demo request.
                  </span>
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  className="w-full sm:flex-1 py-4 px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-base shadow-lg shadow-indigo-200"
                >
                  <FiCheck className="w-5 h-5" />
                  Book Demo Now
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full sm:w-auto px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-all text-base"
                >
                  Cancel
                </button>
              </div>

              <p className="text-[11px] text-slate-400 text-center leading-normal">
                Protected by Google reCAPTCHA.{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">
                  Terms
                </a>
                {' '}apply.
              </p>

            </form>
          </div>
        </motion.div>
      </main>

      {/* reCAPTCHA Verification Submission Modal */}
      <AnimatePresence>
        {showCaptchaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 relative"
            >
              <button
                onClick={() => {
                  setShowCaptchaModal(false);
                  setRecaptchaToken('');
                }}
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>

              <div className="text-center space-y-2 mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl mb-1">
                  <FiShield className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Security Check</h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  Please complete the check below to finish submitting your demo request.
                </p>
              </div>

              <div className="flex justify-center my-4 min-h-[78px]">
                <div id="recaptcha-submission-container" />
              </div>

              {recaptchaError && (
                <p className="text-xs font-semibold text-rose-600 text-center mb-4">
                  {recaptchaError}
                </p>
              )}

              <div className="mt-6 flex flex-col gap-2">
                <button
                  type="button"
                  disabled={!recaptchaToken || loading}
                  onClick={handleFinalSubmit}
                  className="w-full py-3.5 px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm shadow-md"
                >
                  {loading ? (
                    <>
                      <FiLoader className="w-4 h-4 animate-spin" />
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <FiCheck className="w-4 h-4" />
                      Confirm & Submit
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCaptchaModal(false);
                    setRecaptchaToken('');
                  }}
                  className="w-full py-2.5 px-4 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors text-center"
                >
                  Go Back
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DemoRequest;